import { Vector3 } from "@babylonjs/core/Maths/math.vector.js";
import { Control } from "@babylonjs/gui/2D/controls/control.js";
import { StackPanel } from "@babylonjs/gui/2D/controls/stackPanel.js";
import { ObjectBuilder, Image3D, MeshUtils, Rectangle3D, ButtonUtils } from "@dreamcam/babylon-tools";
import { Env } from "../../settings/Env";
import { text } from "../../TextResources";
import { AppView } from "../AppView";
import { ObservableProperty } from "../utils/PropertyUtils";
import { StringUtils } from "../utils/StringUtils";
import { XRInputHelper } from "../XRInputHelper";
export class BroadcastMenuPanelPrivateMode {
    get isVisible() {
        return this._menuMesh.mesh.isVisible;
    }
    set isVisible(value) {
        if (this.isVisible === value)
            return;
        MeshUtils.setVisibility(this._menuMesh, value);
    }
    constructor(menuMesh, menuFunctions, callbacks, messageDialog, loginDialog) {
        this._hideTimeoutMillis = 10000;
        this._privateShowParams = { price: new ObservableProperty(0), minDuration: new ObservableProperty(0) };
        this._audioPrivateShowParams = { price: new ObservableProperty(0), minDuration: new ObservableProperty(0), isEnabled: new ObservableProperty(false) };
        // private _tokens : number = 0;
        this._isGuest = true;
        this._menuMesh = menuMesh;
        this._menuFunctions = menuFunctions;
        this._callbacks = callbacks;
        this._messageDialog = messageDialog;
        this._loginDialog = loginDialog;
        this._addSelectorPanel();
        const pointerAddon = XRInputHelper.addPointerAddon(this._menuMesh);
        pointerAddon.onPointerEnter.add((_mesh) => {
            if (this._hideTimeout !== undefined)
                clearTimeout(this._hideTimeout);
        });
        pointerAddon.onPointerOut.add((_mesh) => {
            this._hideTimeout = setTimeout(() => {
                if (this._menuMesh.mesh.isVisible)
                    this._menuFunctions.setPrivateShowPanelVisibility(false);
            }, this._hideTimeoutMillis);
        });
    }
    _addSelectorPanel() {
        const m = Env.broadcast.menu;
        const cornerRadius = Math.round(this._menuMesh.m2px(m.cornerRadius));
        const rootContainer = new Rectangle3D("rootContainer");
        rootContainer.widthInPixels = this._menuMesh.m2px(m.width);
        rootContainer.heightInPixels = this._menuMesh.m2px(m.privateModePanel.height);
        rootContainer.cornerRadius = cornerRadius;
        rootContainer.thickness = 0;
        rootContainer.background = m.backgroundColor;
        this._menuMesh.texture.addControl(rootContainer);
        const headerPanel = new Rectangle3D("header");
        headerPanel.heightInPixels = this._menuMesh.m2px(m.privateModePanel.headerHeight);
        headerPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        headerPanel.cornerRadius = { topLeft: cornerRadius, topRight: cornerRadius };
        headerPanel.background = m.privateModePanel.headerBackground;
        headerPanel.thickness = 0;
        headerPanel.addControl(ObjectBuilder.createTextBlock3D(this._menuMesh, "privateModePanelHeader", text.broadcast_menu_privateShow_privateModePanelHeader, m.privateModePanel.header));
        const backButton = ButtonUtils.CreateImgButton("backButton", m.privateModePanel.backButton.rectangleLayout, this._menuMesh);
        backButton.onClickObserver.add(() => {
            this._menuFunctions.setPrivateShowPanelVisibility(false);
        });
        const backButtonImage = new Image3D("backButtonImage", m.privateModePanel.backButton.img);
        backButtonImage.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        backButtonImage.verticalAlignment = Control.VERTICAL_ALIGNMENT_CENTER;
        backButton.addControl(backButtonImage);
        headerPanel.addControl(backButton);
        rootContainer.addControl(headerPanel);
        const verticalStackPanel = new StackPanel("privateModePanelStackPanel");
        verticalStackPanel.widthInPixels = this._menuMesh.m2px(m.width);
        verticalStackPanel.heightInPixels = this._menuMesh.m2px(m.privateModePanel.height - m.privateModePanel.headerHeight);
        verticalStackPanel.topInPixels = this._menuMesh.m2px(m.privateModePanel.headerHeight);
        verticalStackPanel.isVertical = true;
        const spacing = this._menuMesh.m2px(m.privateModePanel.spacing);
        verticalStackPanel.spacing = spacing;
        verticalStackPanel.paddingTopInPixels = spacing;
        verticalStackPanel.paddingLeftInPixels = spacing;
        verticalStackPanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_CENTER;
        verticalStackPanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        rootContainer.addControl(verticalStackPanel);
        const rebuildModePanel = (value) => {
            verticalStackPanel.clearControls();
            if (value) {
                verticalStackPanel.addControl(this._addSelectorElement(m.privateModePanel.element.common, m.privateModePanel.element.voicePrivate, text.broadcast_menu_privateShow_audioPrivateMode, m.privateModePanel.style, true, this._audioPrivateShowParams));
            }
            verticalStackPanel.addControl(this._addSelectorElement(m.privateModePanel.element.common, m.privateModePanel.element.textPrivate, text.broadcast_menu_privateShow_textPrivateMode, m.privateModePanel.style, false, this._privateShowParams));
        };
        rebuildModePanel(this._audioPrivateShowParams.isEnabled.Value);
        this._audioPrivateShowParams.isEnabled.Subscribe(value => rebuildModePanel(value));
    }
    _addSelectorElement(common, element, text, style, isAudio, privateParams) {
        const panel = new Rectangle3D("privateModePanelBackground");
        panel.widthInPixels = this._menuMesh.m2px(common.width);
        panel.heightInPixels = this._menuMesh.m2px(common.height);
        panel.cornerRadius = Math.round(this._menuMesh.m2px(common.cornerRadius));
        panel.thickness = 0;
        panel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        panel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        panel.background = common.background;
        const icon = new Image3D("privateShowSelectorIcon", element.icon.url);
        icon.widthInPixels = this._menuMesh.m2px(element.icon.width);
        icon.heightInPixels = this._menuMesh.m2px(element.icon.height);
        icon.leftInPixels = this._menuMesh.m2px(element.icon.left);
        icon.topInPixels = this._menuMesh.m2px(element.icon.top);
        icon.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        icon.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        panel.addControl(icon);
        panel.addControl(ObjectBuilder.createTextBlock3D(this._menuMesh, "privateShowSelectorHeader", text.header, style.header));
        panel.addControl(ObjectBuilder.createTextBlock3D(this._menuMesh, "privateShowSelectorFeatures", text.featureList.join(", "), style.feature));
        const pricePanel = new StackPanel();
        pricePanel.isVertical = false;
        pricePanel.heightInPixels = this._menuMesh.m2px(common.price.height);
        pricePanel.leftInPixels = this._menuMesh.m2px(common.price.left);
        pricePanel.topInPixels = this._menuMesh.m2px(common.price.top);
        pricePanel.horizontalAlignment = Control.HORIZONTAL_ALIGNMENT_LEFT;
        pricePanel.verticalAlignment = Control.VERTICAL_ALIGNMENT_TOP;
        const privateShowPriceText = ObjectBuilder.createTextBlock3D(this._menuMesh, "privateShowPrice", privateParams.price.Value.toString(), style.price);
        pricePanel.addControl(privateShowPriceText);
        const currency = new Image3D("currency", style.currency.img, true);
        currency.widthInPixels = this._menuMesh.m2px(style.currency.width);
        currency.heightInPixels = this._menuMesh.m2px(style.currency.height);
        pricePanel.addControl(currency);
        pricePanel.addControl(ObjectBuilder.createTextBlock3D(this._menuMesh, "priceLabel", text.priceUnits, style.priceLabel));
        panel.addControl(pricePanel);
        const inviteButton = ButtonUtils.CreateImgButtonWithText("privateShowRequestButton", {
            imageLayout: common.button.imageLayout,
            textLayout: common.button.textLayout,
            text: text.buttonText,
        }, this._menuMesh);
        inviteButton.onPointerClickObservable.add(async (eventData, eventState) => {
            if (eventData.buttonIndex !== 0)
                return;
            const itemContainer = eventState.currentTarget;
            const textureHeight = itemContainer.host.getSize().height;
            const deltaMeshY = (textureHeight / 2 - eventData.y) * Env.broadcast.menu.privateModePanel.element.common.height / textureHeight;
            // this._menuMesh.mesh.updateFacetData();
            // const tipMenuAbsolutePosition = this._menuMesh.mesh.getAbsolutePosition();
            // const normal: Vector3 = this._menuMesh.mesh.getFacetNormal(0);
            // const delta = normal.scale(Env.broadcast.menu.privateModePanel.messageZ).add(new Vector3(0, deltaMeshY, 0));
            const messageDialogAbsolutePosition = Vector3.TransformCoordinates(new Vector3(0, deltaMeshY, Env.broadcast.menu.tipMenu.message.z), this._menuMesh.mesh._localMatrix);
            if (this._isGuest) {
                this._messageDialog.openSignInMessage(messageDialogAbsolutePosition, this._menuMesh.mesh.rotationQuaternion ?? this._menuMesh.mesh.rotation.toQuaternion(), async () => {
                    this._messageDialog.hide();
                    //this._menuFunctions.animateClientPanel();
                    if (AppView.getInstance().isApiAuth)
                        this._loginDialog.show();
                    else
                        await AppView.getInstance().loginAsync();
                });
                return;
            }
            // if (this._tokens < privateParams.price.Value * privateParams.minDuration.Value) {
            //     this._messageDialog.openTopUpMessage(messageDialogAbsolutePosition, async () => {
            //         this._messageDialog.hide();
            //         //this._menuFunctions.animateClientPanel();
            //         await AppView.getInstance().topUpAsync();
            //     });
            //     return;
            // }
            await this._menuFunctions.sendPrivateShowRequestAsync(isAudio);
            this._menuFunctions.setPrivateShowPanelVisibility(false);
        });
        panel.addControl(inviteButton);
        const privateShowMinDurationText = ObjectBuilder.createTextBlock3D(this._menuMesh, "privateShowMinDuration", StringUtils.stringFormat(text.minDuration, privateParams.minDuration.Value), style.minDuration);
        panel.addControl(privateShowMinDurationText);
        const updatePrice = (value) => {
            privateShowPriceText.text = value.toString();
            privateShowPriceText._markAsDirty(); // A patch to fix Babylon's bug.
        };
        const updateMinDuration = (value) => {
            privateShowMinDurationText.text = StringUtils.stringFormat(text.minDuration, value);
            privateShowMinDurationText._markAsDirty(); // A patch to fix Babylon's bug.
        };
        privateParams.price.Unsubscribe(updatePrice);
        privateParams.price.Subscribe(updatePrice);
        privateParams.minDuration.Unsubscribe(updateMinDuration);
        privateParams.minDuration.Subscribe(updateMinDuration);
        // Change value twice to guarantee the event:
        const price = privateParams.price.Value;
        privateParams.price.Value = 0;
        privateParams.price.Value = price;
        // Change value twice to guarantee the event:
        const minDuration = privateParams.minDuration.Value;
        privateParams.minDuration.Value = 0;
        privateParams.minDuration.Value = minDuration;
        return panel;
    }
    outputPrivateShowTokensPerMin(value) {
        this._privateShowParams.price.Value = value;
    }
    outputPrivateShowMinutesMin(value) {
        this._privateShowParams.minDuration.Value = value;
    }
    outputAudioPrivateShowTokensPerMin(value) {
        this._audioPrivateShowParams.price.Value = value;
    }
    outputAudioPrivateShowMinutesMin(value) {
        this._audioPrivateShowParams.minDuration.Value = value;
    }
    outputAudioPrivateShowEnabled(value) {
        this._audioPrivateShowParams.isEnabled.Value = value;
    }
    outputClientBalance(_tokens) {
        // this._tokens = tokens;
    }
    stateChanged(_state, isGuest, _reason) {
        this._isGuest = isGuest;
    }
}
